import { FC, ReactNode } from 'react';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import { Avatar, Stack, SxProps, Typography } from '@mui/material';

import { ListItem } from './ListItem';

import { PAYMENT_METHOD } from '~common';
import { LocalizationTypography } from '~components/organisms/localization';
import { useUserAgent } from '~hooks';
import { CampusCardSummary, PaymentInfo } from '~types';
import { getCardImage, properCardBrandName } from '~utils';

interface Props {
  enableDivider?: boolean;
  action: ReactNode;
  iconWidth?: number;
  iconHeight?: number;
  onClick?: () => void;
}

interface MethodItemProps extends Props {
  imgSrc?: string;
  imgAlt?: string;
  icon?: ReactNode;
  title: ReactNode;
  leftContainerStyle?: SxProps;
  rightContainerStyle?: SxProps;
}

function isCardExpired(card: PaymentInfo) {
  return (
    card.cardExpYear < new Date().getFullYear() ||
    (card.cardExpYear === new Date().getFullYear() &&
      card.cardExpMonth < new Date().getMonth() + 1)
  );
}

export const MethodItem: FC<MethodItemProps> = ({
  imgSrc,
  imgAlt,
  icon,
  title,
  enableDivider = true,
  action,
  iconWidth = 30,
  iconHeight = 30,
  onClick,
  leftContainerStyle,
  rightContainerStyle,
}) => {
  return (
    <ListItem
      leftContainerStyle={leftContainerStyle}
      rightContainerStyle={rightContainerStyle}
      left={
        icon ?? (
          <img
            src={imgSrc}
            alt={imgAlt}
            height={iconHeight}
            width={iconWidth}
          />
        )
      }
      right={
        <>
          {typeof title === 'string' ? (
            <Typography variant="Poppins/Subheading-md" color="#111928">
              {title}
            </Typography>
          ) : (
            title
          )}
          {action}
        </>
      }
      enableDivider={enableDivider}
      onContainerClick={onClick}
    />
  );
};

interface PaymentMethodItemProps extends Props {
  card?: PaymentInfo;
}

export const PaymentMethodItem: FC<PaymentMethodItemProps> = ({
  card,
  enableDivider = true,
  action,
  iconWidth = 30,
  iconHeight = 30,
  onClick,
}) => {
  if (!card) {
    return (
      <MethodItem
        imgSrc={'/images/card-icons/credit-card.svg'}
        imgAlt="payment"
        title="Debit/Credit Card"
        enableDivider={enableDivider}
        action={action}
        onClick={onClick}
      />
    );
  }

  return (
    <ListItem
      enableDivider={enableDivider}
      left={
        <img
          src={getCardImage(card.method, card.cardBrand)}
          alt="payment"
          height={iconHeight}
          width={iconWidth}
        />
      }
      right={
        <>
          <Stack direction="column">
            <Typography variant="Poppins/Subheading-md" color="#111928">
              {properCardBrandName(card.cardBrand)} •••• {card.cardLast4}
            </Typography>
            {isCardExpired(card) ? (
              <LocalizationTypography
                variant="Inter/Body-sm"
                color="error.main"
                textId="app.rentalPage.newStepThree.rentCharger.cardExpired"
                defaultText="Card is expired."
              />
            ) : (
              <LocalizationTypography
                variant="Inter/Body-sm"
                color="#6B7280"
                textId="app.rentalPage.newStepThree.rentCharger.expirationDate"
                defaultText="Expiration: {{value}}"
                textValues={{
                  value: `${card.cardExpMonth}/${card.cardExpYear}`,
                  interpolation: {
                    escapeValue: false, // allow interpolation ("/" in this case) to be used
                  },
                }}
              />
            )}
          </Stack>
          {action}
        </>
      }
      onContainerClick={onClick}
    />
  );
};

export const WalletPaymentMethodItem: FC<Props> = ({
  enableDivider = true,
  action,
  onClick,
}) => {
  const { availableWalletPaymentMethod } = useUserAgent();

  const imgSrc =
    availableWalletPaymentMethod === PAYMENT_METHOD.APPLE_PAY
      ? '/images/card-icons/apple.svg'
      : '/images/card-icons/google_pay.svg';

  const title =
    availableWalletPaymentMethod === PAYMENT_METHOD.APPLE_PAY
      ? 'Apple Pay'
      : 'Google Pay';

  return (
    <MethodItem
      imgSrc={imgSrc}
      imgAlt="wallet-payment"
      title={title}
      enableDivider={enableDivider}
      action={action}
      onClick={onClick}
    />
  );
};

interface CampusCardItemProps {
  card: CampusCardSummary;
  enableDivider?: boolean;
  action?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}

export const CampusCardItem: FC<CampusCardItemProps> = ({
  card,
  enableDivider = true,
  action,
  onClick,
  disabled = false,
}) => {
  return (
    <MethodItem
      enableDivider={enableDivider}
      leftContainerStyle={{ flex: 'none', marginRight: 2 }}
      rightContainerStyle={
        disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined
      }
      icon={
        <Avatar
          sx={{
            width: 54,
            height: 54,
            bgcolor: (theme) => theme.palette.neutral[200],
          }}
        >
          <CreditCardOutlinedIcon
            sx={{
              color: (theme) => theme.palette.text.secondary,
              fontSize: 30,
            }}
          />
        </Avatar>
      }
      title={
        <Stack direction="column">
          <Typography variant="Poppins/Subheading-md" color="text.primary">
            {card.institutionName}
          </Typography>
          {card.programName && (
            <Typography variant="Inter/Body-sm" color="text.secondary">
              {card.programName}
            </Typography>
          )}
        </Stack>
      }
      action={action}
      onClick={disabled ? undefined : onClick}
    />
  );
};
